/**
 * Reseller change-request inbox — list of customer change requests
 * (v142) with a detail view to resolve / decline with notes. Resolution
 * triggers a branded email to the customer via
 * resellerNotifications.changeRequestResolved.
 */
import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import ResellerShell from './ResellerShell'
import { useLanguage } from '../../context/LanguageContext'
import { tournamentApi } from '../../services/api'

export default function ChangeRequests() {
  const { t } = useLanguage()
  const { id } = useParams()
  return (
    <ResellerShell title={id ? t('sp_cr_title_singular', 'Change request') : t('sp_cr_title_plural', 'Change requests')}>
      {id ? <Detail id={Number(id)} /> : <List />}
    </ResellerShell>
  )
}

function statusLabel(t, s) {
  switch (s) {
    case 'open': return t('sp_cr_status_open', 'open')
    case 'in_progress': return t('sp_cr_status_in_progress', 'in progress')
    case 'resolved': return t('sp_cr_status_resolved', 'resolved')
    case 'declined': return t('sp_cr_status_declined', 'declined')
    case 'all': return t('sp_cr_status_all', 'all')
    default: return s.replace(/_/g, ' ')
  }
}

function List() {
  const { t } = useLanguage()
  const [status, setStatus] = useState('open')
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    setData(null)
    tournamentApi.get('/portal/reseller/change-requests', { params: { status } })
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.message || e.message))
  }, [status])

  if (err) return <div style={errorBox}>{err}</div>

  return (
    <>
      <p style={{ color: '#64748b', fontSize: 14, lineHeight: 1.6, maxWidth: 720 }}>
        {t('sp_cr_list_blurb', 'Customers can submit structured change requests from their trip portal. Resolve, decline, or send back a note — the customer gets an email each time.')}
      </p>
      <div style={{ display: 'flex', gap: 6, margin: '16px 0' }}>
        {['open','in_progress','resolved','declined','all'].map(s => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s === status ? 'all' : s)}
            style={{
              padding: '6px 12px',
              fontSize: 13,
              border: '1px solid ' + (s === status ? '#0f172a' : '#cbd5e1'),
              background: s === status ? '#0f172a' : '#fff',
              color: s === status ? '#fff' : '#334155',
              borderRadius: 6,
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontWeight: 600,
            }}
          >{statusLabel(t, s)}</button>
        ))}
      </div>
      {!data ? (
        <div style={{ color: '#64748b' }}>{t('sp_cr_loading_requests', 'Loading requests...')}</div>
      ) : data.requests.length === 0 ? (
        <div style={{ color: '#64748b' }}>{t('sp_cr_no_requests', 'No change requests in this view.')}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.requests.map(r => (
            <button
              key={r.id}
              onClick={() => navigate('/portal/reseller/change-requests/' + r.id)}
              style={{
                textAlign: 'left',
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                padding: 14,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {t('sp_cr_request_booking_golfer', 'Request #{id} · Booking #{bookingId} · {golfer}', { id: r.id, bookingId: r.bookingId, golfer: r.golferName || r.golferEmail })}
                </div>
                <span style={statusPill(r.status)}>{statusLabel(t, r.status)}</span>
              </div>
              <div style={{ color: '#64748b', fontSize: 13, marginBottom: 4 }}>{t('sp_cr_package_checkin', '{packageName} · check-in {checkIn}', { packageName: r.packageName, checkIn: r.checkIn })}</div>
              <div style={{ color: '#475569', fontSize: 13 }}>
                {t('sp_cr_line_item_count', '{count} line item{plural}', { count: r.itemCount, plural: r.itemCount === 1 ? '' : 's' })}
                {r.additionalNotes ? ' ' + t('sp_cr_note_snippet', '· note: {note}', { note: `${r.additionalNotes.slice(0, 80)}${r.additionalNotes.length > 80 ? '...' : ''}` }) : ''}
              </div>
              <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 4 }}>{new Date(r.createdAt).toLocaleString()}</div>
            </button>
          ))}
        </div>
      )}
    </>
  )
}

function Detail({ id }) {
  const { t } = useLanguage()
  const VERBS = {
    keep: t('sp_cr_verb_keep', 'Keep with note'),
    change_datetime: t('sp_cr_verb_change_datetime', 'Change date / time'),
    cancel: t('sp_cr_verb_cancel', 'Cancel this item'),
    other: t('other', 'Other'),
  }
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [resolution, setResolution] = useState('')
  const [target, setTarget] = useState('resolved')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  function reload() {
    return tournamentApi.get('/portal/reseller/change-requests/' + id)
      .then(r => {
        setData(r.data)
        setResolution(r.data.request.resolutionNotes || '')
      })
      .catch(e => setErr(e?.response?.data?.message || e.message))
  }
  useEffect(() => { reload() // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  async function submit() {
    if ((target === 'resolved' || target === 'declined') && !resolution.trim()) {
      setErr(t('sp_cr_note_required', 'Please include a note for the customer.'))
      return
    }
    setSubmitting(true); setErr(null)
    try {
      await tournamentApi.post('/portal/reseller/change-requests/' + id + '/resolve', {
        status: target, resolutionNotes: resolution.trim() || null,
      })
      setDone(true)
      await reload()
    } catch (e) {
      setErr(e?.response?.data?.message || e.message)
    } finally {
      setSubmitting(false)
    }
  }

  if (err && !data) return <div style={errorBox}>{err}</div>
  if (!data) return <div style={{ color: '#64748b' }}>{t('loading_ellipsis', 'Loading...')}</div>

  const { request, booking, items } = data
  const terminal = ['resolved', 'declined'].includes(request.status)

  return (
    <>
      <Link to="/portal/reseller/change-requests" style={{ color: '#64748b', fontSize: 13, textDecoration: 'none' }}>{t('sp_cr_all_change_requests', '← All change requests')}</Link>

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginTop: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ fontWeight: 600, fontSize: 16 }}>{t('sp_cr_request_number', 'Request #{id}', { id: request.id })}</div>
          <span style={statusPill(request.status)}>{statusLabel(t, request.status)}</span>
        </div>
        <div style={{ color: '#64748b', fontSize: 13 }}>
          {t('sp_cr_from_golfer', 'From {golfer} ·', { golfer: booking.golferName || booking.golferEmail })}
          {' '}<Link to={'/portal/reseller/bookings/' + booking.id} style={{ color: '#0066cc', textDecoration: 'none' }}>{t('sp_booking_number_title', 'Booking #{id}', { id: booking.id })}</Link> ·
          {' '}{booking.packageName} · {booking.checkIn} → {booking.checkOut}
        </div>
        <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 6 }}>{t('sp_cr_submitted_at', 'Submitted {datetime}', { datetime: new Date(request.createdAt).toLocaleString() })}</div>
      </div>

      <h2 style={{ fontSize: 13, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, margin: '24px 0 8px' }}>{t('sp_cr_line_items_heading', 'Line items')}</h2>
      {items.length === 0 ? (
        <div style={{ color: '#94a3b8', fontSize: 13 }}>{t('sp_cr_no_line_items', '(No specific line items — only an additional note below.)')}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map(i => (
            <div key={i.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{i.label || (i.componentType === 'hotel' ? t('sp_th_hotel', 'Hotel') : t('sp_cr_round_n', 'Round {n}', { n: (i.roundIndex ?? 0) + 1 }))}</div>
              <div style={{ marginTop: 4, fontSize: 13, color: '#475569' }}>{VERBS[i.action] || i.action}</div>
              {i.notes && (
                <div style={{ marginTop: 6, padding: 8, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 13, whiteSpace: 'pre-wrap', color: '#334155' }}>
                  {i.notes}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {request.additionalNotes && (
        <>
          <h2 style={{ fontSize: 13, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, margin: '20px 0 8px' }}>{t('sp_cr_additional_notes_heading', 'Additional notes')}</h2>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, fontSize: 13, whiteSpace: 'pre-wrap', color: '#334155' }}>
            {request.additionalNotes}
          </div>
        </>
      )}

      <h2 style={{ fontSize: 13, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, margin: '24px 0 8px' }}>
        {terminal ? t('sp_cr_resolution_heading', 'Resolution') : t('sp_cr_respond_to_customer_heading', 'Respond to the customer')}
      </h2>
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
        {terminal ? (
          <div style={{ fontSize: 13, color: '#334155', whiteSpace: 'pre-wrap' }}>
            {request.resolutionNotes || t('sp_cr_no_note', '(no note)')}
          </div>
        ) : (
          <>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>{t('sp_cr_whats_the_status', "What's the status?")}</label>
            <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
              {['in_progress','resolved','declined'].map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setTarget(s)}
                  style={{
                    padding: '6px 12px',
                    fontSize: 13,
                    border: '1px solid ' + (s === target ? '#0f172a' : '#cbd5e1'),
                    background: s === target ? '#0f172a' : '#fff',
                    color: s === target ? '#fff' : '#334155',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontFamily: 'inherit',
                  }}
                >{statusLabel(t, s)}</button>
              ))}
            </div>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>
              {t('sp_cr_note_to_customer', 'Note to the customer')} {target !== 'in_progress' && <span style={{ color: '#dc2626' }}>*</span>}
            </label>
            <textarea
              value={resolution}
              onChange={e => setResolution(e.target.value)}
              placeholder={target === 'resolved' ? t('sp_cr_placeholder_resolved', 'How you handled it...') : target === 'declined' ? t('sp_cr_placeholder_declined', 'Why this could not be done...') : t('sp_cr_placeholder_in_progress', 'Status update for the customer (optional)')}
              rows={5}
              maxLength={4000}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
            />
            {err && <div style={{ ...errorBox, marginTop: 10 }}>{err}</div>}
            {done && <div style={{ marginTop: 10, color: '#065f46', fontSize: 13 }}>{t('sp_cr_saved_notified', 'Saved. Customer notified by email.')}</div>}
            <div style={{ marginTop: 12 }}>
              <button type="button" onClick={submit} disabled={submitting} style={primaryBtn}>
                {submitting ? t('sp_saving', 'Saving...') : (target === 'in_progress' ? t('sp_cr_save_update_customer', 'Save & update customer') : (target === 'resolved' ? t('sp_cr_save_mark_resolved', 'Save & mark resolved') : t('sp_cr_save_decline', 'Save & decline')))}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  )
}

function statusPill(status) {
  const map = {
    open:        { bg: '#fef3c7', fg: '#92400e' },
    in_progress: { bg: '#dbeafe', fg: '#1e40af' },
    resolved:    { bg: '#d1fae5', fg: '#065f46' },
    declined:    { bg: '#fee2e2', fg: '#991b1b' },
  }
  const c = map[status] || { bg: '#e2e8f0', fg: '#475569' }
  return { display: 'inline-block', padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: c.bg, color: c.fg, textTransform: 'uppercase', letterSpacing: 0.5 }
}

const primaryBtn = { padding: '10px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const errorBox = { padding: 10, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, fontSize: 13 }
